import { env } from './env';

// API Configuration
export const API_CONFIG = {
  baseUrl: env.apiBaseUrl,
  assessmentServiceBaseUrl: env.assessmentServiceBaseUrl,
  complianceRequirementsServiceBaseUrl: env.complianceRequirementsServiceBaseUrl,
  externalServiceBaseUrl: env.externalServiceBaseUrl,
  authServiceUrl: env.authServiceUrl,
  locationServiceBaseUrl: env.locationServiceBaseUrl,
  vehicleServiceBaseUrl: env.vehicleServiceBaseUrl,
  userServiceBaseUrl: env.userServiceBaseUrl,
  timeout: 30000, // 30 seconds
  // Cache configuration (in milliseconds). Default: 5 minutes (300000ms)
  cacheDuration: {
    lookupTables: env.cacheLookupTablesMs,
  },
  // Cache invalidation strategy
  cacheInvalidation: {
    // This setting controls whether to invalidate the lookup tables cache after requirement mutations
    // (create, update, delete, archive, deactivate).
    // Note: Requirements and lookup tables are separate data; requirement mutations do not directly
    // modify lookup tables. This setting is useful for development/testing scenarios.
    //
    // false (default): Use time-based expiry only (better performance)
    // true: Invalidate cache after every mutation (for development/testing)
    //
    // Most apps should use false - requirements don't modify lookup tables
    invalidateOnMutation: env.invalidateCacheOnMutation,
  },
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Assessment endpoints - GET (fetch status)
  assessment: {
    driver: (driverId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/driver/${driverId}/status`,
    monitor: (monitorId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/monitor/${monitorId}/status`,
    vehicle: (vehicleId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/vehicle/${vehicleId}/status`,
  },
  // Assessment run endpoints - POST (run compliance)
  runAssessment: {
    driver: (driverId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/driver/${driverId}`,
    monitor: (monitorId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/monitor/${monitorId}`,
    vehicle: (vehicleId: string) => `${API_CONFIG.assessmentServiceBaseUrl}/assessment/vehicle/${vehicleId}`,
  },
  // Lookup tables endpoint
  lookupTables: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/lookupTables`,

  // Scopes endpoint
  scopes: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/scopes`,

  // Districts endpoints
  districts: {
    schoolDistricts: () => `${API_CONFIG.locationServiceBaseUrl}/districts/school-districts`,
    states: () => `${API_CONFIG.locationServiceBaseUrl}/districts/states`,
  },

  // Requirements by scope endpoint (POST)
  requirementsByScope: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/by-scope`,

  // Compliance Requirements endpoints (CRUD operations - still actively used)
  // Note: These are the primary endpoints for creating, updating, and deleting requirements
  complianceRequirements: {
    list: (tab?: string) => {
      const tabParam = tab ? `?tab=${tab}` : '';
      return `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements${tabParam}`;
    },
    get: (id: string) => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/${id}`,
    create: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements`,
    update: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements`,
    delete: (id: string) => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/${id}`,
    updateStatus: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/update-status`,
    enable: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/enable`,
    bulkUploadDocuments: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/documents/bulk-upload`,
    deleteDocuments: () => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/documents`,
    downloadDocument: (documentId: string) => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/documents/${documentId}/file`,
    copy: (id: string) => `${API_CONFIG.complianceRequirementsServiceBaseUrl}/requirements/${id}/copy`,
  },
  checkrVerification: {
    readiness: () => `${API_CONFIG.externalServiceBaseUrl}/checkr/verifications/readiness`,
    start: () => `${API_CONFIG.externalServiceBaseUrl}/checkr/verifications/start`,
    history: () => `${API_CONFIG.externalServiceBaseUrl}/checkr/verifications/history`,
  },
  // User Service endpoints
  users: {
    // Optional spId scopes the drivers list to a specific SP. Required for
    // internal-user callers (their JWT has no service_provider_id claim); SpStaff
    // omit it and the endpoint falls back to their JWT claim.
    drivers: (spId?: string) => {
      const base = `${API_CONFIG.userServiceBaseUrl}/users/drivers`;
      return spId ? `${base}?serviceProviderId=${encodeURIComponent(spId)}` : base;
    },
    driverGeo: (driverId: string) => `${API_CONFIG.userServiceBaseUrl}/users/drivers/${driverId}/geo`,
  },
  // Vehicle Service endpoints
  vehicles: {
    fleet: () => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/fleet`,
    getById: (id: string) => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/${id}`,
    byDriver: (driverId: string) => `${API_CONFIG.vehicleServiceBaseUrl}/Vehicles/driver/${driverId}`,
    create: () => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles`,
    update: (id: string) => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/${id}`,
    delete: (id: string) => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/${id}`,
    updateStatus: (id: string) => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/${id}/status`,
    bulkUpdateStatus: () => `${API_CONFIG.vehicleServiceBaseUrl}/vehicles/status/bulk`,
    lookupTables: () => `${API_CONFIG.vehicleServiceBaseUrl}/lookuptables`,
  },
} as const;

// API configuration constants
export const PAGINATION_DEFAULTS = {
  pageSize: 25,
  pageNumber: 1,
} as const;

// Scope name mappings (tab to scope name)
export const SCOPE_MAPPINGS = {
  everdriven: 'EverDriven',
  states: 'States',
  districts: 'Districts',
} as const;
